import React, { useState } from 'react';
import './FeedbackButtons.css';

interface FeedbackButtonsProps {
  eventId: number;
  onFeedback: (eventId: number, type: 'positive' | 'negative', comment?: string) => Promise<void>;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ eventId, onFeedback }) => {
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);

  const handlePositiveFeedback = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onFeedback(eventId, 'positive');
      setSubmitted(true);
      setFeedbackType('positive');
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Feedback submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNegativeFeedback = () => {
    setShowCommentBox(true);
    setFeedbackType('negative');
  };

  const handleSubmitComment = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onFeedback(eventId, 'negative', comment || undefined);
      setSubmitted(true);
      setShowCommentBox(false);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Feedback submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowCommentBox(false);
    setComment('');
    setFeedbackType(null);
  };

  if (submitted) {
    return (
      <div className="feedback-buttons submitted">
        <span className="feedback-thanks">
          {feedbackType === 'positive' ? '👍 Thanks for your feedback!' : '👎 Thanks for your feedback!'}
        </span>
      </div>
    );
  }

  return (
    <div className="feedback-buttons">
      {!showCommentBox ? (
        <div className="feedback-button-group">
          <button
            className="feedback-button positive"
            onClick={handlePositiveFeedback}
            disabled={submitting}
            title="This response was helpful"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 8.5V14.5H2.5C1.67 14.5 1 13.83 1 13V10C1 9.17 1.67 8.5 2.5 8.5H4ZM4 8.5C4 6.29 5.79 4.5 8 4.5C8.55 4.5 9 4.95 9 5.5V7.5L11.5 7.5C12.33 7.5 13 8.17 13 9C13 9.28 12.92 9.54 12.78 9.76L11 13.5C10.78 13.96 10.31 14.5 9.5 14.5H6C4.9 14.5 4 13.6 4 12.5V8.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className="feedback-button negative"
            onClick={handleNegativeFeedback}
            disabled={submitting}
            title="This response needs improvement"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 7.5V1.5H13.5C14.33 1.5 15 2.17 15 3V6C15 6.83 14.33 7.5 13.5 7.5H12ZM12 7.5C12 9.71 10.21 11.5 8 11.5C7.45 11.5 7 11.05 7 10.5V8.5L4.5 8.5C3.67 8.5 3 7.83 3 7C3 6.72 3.08 6.46 3.22 6.24L5 2.5C5.22 2.04 5.69 1.5 6.5 1.5H10C11.1 1.5 12 2.4 12 3.5V7.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      ) : (
        <div className="feedback-comment-box">
          <div className="feedback-header">
            <p className="feedback-prompt">Help us improve</p>
            <p className="feedback-subtitle">
              Your feedback helps us understand what went wrong and make Nomi better for everyone.
            </p>
          </div>
          <div className="feedback-input-wrapper">
            <textarea
              className="feedback-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What could be improved? For example:&#10;• The response was inaccurate or incomplete&#10;• The SQL query didn't work as expected&#10;• The format was confusing&#10;• Something else..."
              rows={6}
              disabled={submitting}
              maxLength={500}
            />
            <div className="feedback-char-count">
              {comment.length} / 500 characters
            </div>
          </div>
          <div className="feedback-actions">
            <button
              className="feedback-cancel"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="feedback-submit"
              onClick={handleSubmitComment}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </div>
      )}
      {error && <p className="feedback-error">{error}</p>}
    </div>
  );
};
